import { useEffect, useState } from "react";
import ProfitIcon from "./dashboard/icons/ProfitIcon";
import { useSelector } from "react-redux";
import ReactCardFlip from "react-card-flip";
import {
  Typography,
  Box,
  Table,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  Paper,
  TableContainer,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ThreeSixtyIcon from "@mui/icons-material/ThreeSixty";
import { useTranslation } from "react-i18next";

const generateElectricityConsumedPayload = (
  data,
  facilityFilter,
  currency = null
) => {
  return [
    {
      type: "Electricity Bill",
      units: data.units_consumed?.toFixed(2),
      price:
        (currency ? currency : " ") +
        facilityFilter?.price_per_unit_electricity?.toFixed(2),
      amount: data.electricity_total?.toFixed(2),
    },
  ];
};

const ElectricityConsumed = ({ stats }) => {
  const [electricityConsumed, setElectricityConsumed] = useState(0.0);
  const { currency } = useSelector((state) => state.user);
  const stationlist = useSelector((state) => state.stationList.data.data);
  const { selectedFacility } = useSelector((state) => state.stationList);
  const [rows, setRows] = useState(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    if (stats) {
      setElectricityConsumed(
        !isNaN(parseFloat(stats?.electricity_total))
          ? Number(stats.electricity_total.toFixed(2)).toLocaleString()
          : 0.0
      );

      if (selectedFacility?.is_fleet) {
        setRows(generateElectricityConsumedPayload(stats, selectedFacility));
      }
    }
    setLoading(false);
  }, [stats, selectedFacility, stationlist, currency]);

  const handleFlip = () => {
    setIsFlipped((prev) => !prev);
  };

  return (
    <Box>
      <ReactCardFlip
        isFlipped={isFlipped}
        flipDirection="horizontal"
        flipSpeedFrontToBack="0.6"
        flipSpeedBackToFront="0.6"
      >
        <Box
          display={"flex"}
          alignItems={"center"}
          bgcolor={"white"}
          justifyContent={"space-between"}
          borderRadius={"16px"}
          padding="24px"
          height="130px"
          key="front"
        >
          <Typography>
            <Typography
              variant="subtitle"
              fontSize="12px"
              fontWeight="600"
              style={{ textWrap: "nowrap" }}
            >
              {t("electricityBill")}
              {loading ? (
                <span style={{ color: "#467CEB" }}> {t("loading")}...</span>
              ) : (
                ""
              )}
            </Typography>
            <Typography
              variant="h5"
              fontSize="24px"
              fontWeight="700"
              sx={{ marginTop: "10px" }}
              style={{ textWrap: "nowrap" }}
            >{`${currency} ${electricityConsumed}`}</Typography>
          </Typography>
          <ProfitIcon />
          {selectedFacility?.is_fleet && (
            <Tooltip
              title="Info"
              sx={{
                position: "absolute",
                top: "-3px",
                right: "0px",
                color: "grey",
              }}
            >
              <IconButton>
                <Box onClick={handleFlip}>
                  <InfoOutlinedIcon />
                </Box>
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {selectedFacility?.is_fleet && (
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            borderRadius={"16px"}
            height="130px"
            key="back"
            overflow="hidden"
          >
            <TableContainer
              component={Paper}
              sx={{
                height: "100%",
                width: "100%",
                overflow: "hidden",
                padding: "5px 5px 0",
                border: "none",
                backgroundColor: "grey",
                color: "white",
              }}
            >
              <Typography
                variant="h5"
                fontSize="12px"
                fontWeight="600"
                padding="5px"
                textAlign="center"
              >
                ELECTRICITY BILL
              </Typography>
              <Table aria-label="simple table" sx={{ margin: "10px 0 0" }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      align="center"
                      sx={{ padding: "2px", fontSize: "11px", color: "white" }}
                    >
                      Type
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ padding: "2px", fontSize: "11px", color: "white" }}
                    >
                      Units&nbsp;(kWh)
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ padding: "2px", fontSize: "11px", color: "white" }}
                    >
                      {currency || " "}/kWh
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ padding: "2px", fontSize: "11px", color: "white" }}
                    >
                      Amount
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows &&
                    rows.map((row) => (
                      <TableRow
                        key={row.type}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell
                          component="th"
                          scope="row"
                          align="center"
                          sx={{
                            padding: "2px",
                            fontSize: "10px",
                            color: "white",
                          }}
                        >
                          {row.type}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            padding: "2px",
                            fontSize: "8px",
                            color: "white",
                          }}
                        >
                          {row.units}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            padding: "2px",
                            fontSize: "8px",
                            color: "white",
                          }}
                        >
                          {row.price}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            padding: "2px",
                            fontSize: "8px",
                            color: "white",
                          }}
                        >
                          {currency || " "}
                          {row.amount}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Tooltip
              title="Rotate"
              sx={{
                position: "absolute",
                top: "-3px",
                right: "0px",
                color: "white",
              }}
            >
              <IconButton>
                <Box onClick={handleFlip}>
                  <ThreeSixtyIcon />
                </Box>
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </ReactCardFlip>
    </Box>
  );
};
export default ElectricityConsumed;
